import { Box, Typography } from "@mui/material";

import { colors } from "../theme/tokens";

import type { WidgetProps } from "./types";

// Company-scale figures as the brand's figure row (Bison-brand v1.0.0,
// "Figure row"; the site's FeatureList close): one ruled row, a hairline
// above and below, each figure at the section scale in extrabold tabular
// navy over a small label in body gray. Until 2026-10-26 each figure sat in
// an outlined tile; the brand keeps cards for links, so static figures are
// open type on the band. A `highlight` figure still reads orange. Static
// canonical copy: no snapshot line and no source note, which is what
// metric_tiles is for on the Opportunity page.
export function StatBand({ widget }: WidgetProps<"stat_band">) {
    const { items, cards } = widget.props;

    // Opt-in card view (exclusivity pitch room, Elijah 2026-10-27): each item an
    // outlined card with its brand icon, the value in navy, never orange.
    if (cards) {
        return (
            <Box
                sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: { xs: "1fr", sm: `repeat(${Math.min(items.length, 3)}, 1fr)` },
                }}
            >
                {items.map((item) => (
                    <Box
                        key={item.label}
                        sx={{ p: 3, border: 1, borderColor: colors.line, borderRadius: "10px", bgcolor: "background.paper" }}
                    >
                        {item.icon && (
                            <Box component="img" src={item.icon} alt="" aria-hidden sx={{ display: "block", width: 40, height: 40, mb: 2 }} />
                        )}
                        <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, lineHeight: 1.25, color: colors.navy }}>
                            {item.value}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, lineHeight: 1.5, color: "text.secondary" }}>
                            {item.label}
                        </Typography>
                    </Box>
                ))}
            </Box>
        );
    }

    return (
        <Box
            component="dl"
            sx={{
                m: 0,
                py: 4,
                borderTop: 1,
                borderBottom: 1,
                borderColor: colors.line,
                display: "grid",
                rowGap: 4,
                gridTemplateColumns: {
                    xs: "repeat(2, 1fr)",
                    sm: "repeat(3, 1fr)",
                    md: `repeat(${items.length}, 1fr)`,
                },
            }}
        >
            {items.map((item) => (
                <Box key={item.label} sx={{ minWidth: 0, px: 1, textAlign: "center" }}>
                    <Typography
                        component="dt"
                        sx={{
                            fontSize: { xs: "1.875rem", sm: "2.25rem" },
                            fontWeight: 800,
                            letterSpacing: "-0.02em",
                            lineHeight: 1,
                            fontVariantNumeric: "tabular-nums",
                            color: item.highlight ? colors.brandOrange : colors.navy,
                        }}
                    >
                        {item.value}
                    </Typography>
                    <Typography
                        component="dd"
                        variant="body2"
                        sx={{ m: 0, mt: 1, mx: "auto", maxWidth: "13rem", lineHeight: 1.375, color: "text.secondary" }}
                    >
                        {item.label}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}
